'use client'
import { useRef, type FC } from 'react'
import styled from 'styled-components'
import { motion, useInView } from 'motion/react'
import { SectionMediumFullWidth } from '../SectionMediumFullWidth'
import { defaultGrid, designGrid, innerGrid, mq, presetsFluid, rgbAlpha, VARIANTS_FADE_FROM_BOTTOM_TO_TOP, VARIANTS_FADE_FROM_LEFT_TO_RIGHT, VARIANTS_STAGGER_CHILDREN, vw } from '@/styles'
import { useTranslator } from '@/hooks'
import { useParams } from 'next/navigation'
import NextImage from 'next/image'
import { diagonalSvg } from '../layout/AsidePreFooter'

type Props = {
  channelKey: 'haberdasheries' | 'industry' | 'wholesaler'
}

/**
 * This should match the structure in the dictionaries exactly
 */
type CopiesProps = {
  text: string,
  list_title: string,
  list_items: Array<{
    title: string,
    description: string
  }>,
  services: {
    title: string,
    description: string,
    list_items: Array<{
      title: string,
      description: string
    }>
  }
}

const mediumFullWidthDataBySlug = {
  haberdasheries: {
    type: 'video' as const,
    src: '/media/channels/haberdasheries/header.mp4',
    alt: 'header video haberdasheries'
  },
  industry: {
    type: 'video' as const,
    src: '/media/channels/industry/header.mp4',
    alt: 'header video industry'
  },
  wholesaler: {
    type: 'video' as const,
    src: '/media/channels/wholesaler/header.mp4',
    alt: 'header video wholesaler'
  }
}


export const MainChannelDetail: FC<Props> = ({ channelKey }) => {
  const { slug } = useParams() as { slug: keyof typeof mediumFullWidthDataBySlug }
  const { t } = useTranslator()
  const refSectionMain = useRef<HTMLElement>(null)
  const refSectionServices = useRef<HTMLElement>(null)
  const isInViewSectionMain = useInView(refSectionMain, { once: true })
  const isInViewSectionServices = useInView(refSectionServices, { once: true })

  return (
    <Main>
      <SectionMediumFullWidth data={{ ...mediumFullWidthDataBySlug[slug], type: 'video'}} />
      <SectionMain ref={refSectionMain}>
        <h2>
          <motion.span initial='initial' animate={isInViewSectionMain ? 'animate' : 'initial'} variants={VARIANTS_FADE_FROM_LEFT_TO_RIGHT({})}>
            {t(`project.channels.${channelKey}.text`) as unknown as CopiesProps['text']}
          </motion.span>
        </h2>
        <motion.dl initial='initial' animate={isInViewSectionMain ? 'animate' : 'initial'} variants={VARIANTS_STAGGER_CHILDREN}>
          <motion.dt variants={VARIANTS_FADE_FROM_BOTTOM_TO_TOP()}>
            <span>
              {t(`project.channels.${channelKey}.list_title`) as unknown as CopiesProps['list_title']}
            </span>
          </motion.dt>
          {(t(`project.channels.${channelKey}.list_items`) as unknown as CopiesProps['list_items'])?.map((item, index) => (
            <motion.dd key={index} variants={VARIANTS_FADE_FROM_BOTTOM_TO_TOP()}>
              <div className='medium-wrapper'>
                {diagonalSvg()}
                <NextImage src={`/media/channels/${slug}/${index + 1}.${(slug === 'industry' && index === 1) ? 'gif' : 'jpg'}`} alt={`Image for ${item.title}`} width={320} height={320} />
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </motion.dd>
          ))}
        </motion.dl>
      </SectionMain>
      <SectionServices ref={refSectionServices}>
        <hgroup>
          <motion.h2 initial='initial' animate={isInViewSectionServices ? 'animate' : 'initial'} variants={VARIANTS_FADE_FROM_BOTTOM_TO_TOP(.4)}>
            {t(`project.channels.${channelKey}.services.title`) as unknown as CopiesProps['services']['title']}
          </motion.h2>
          <motion.p initial='initial' animate={isInViewSectionServices ? 'animate' : 'initial'} variants={VARIANTS_FADE_FROM_LEFT_TO_RIGHT({})}>
            {t(`project.channels.${channelKey}.services.description`) as unknown as CopiesProps['services']['description']}
          </motion.p>
        </hgroup>
        <motion.dl initial='initial' animate={isInViewSectionServices ? 'animate' : 'initial'} variants={VARIANTS_STAGGER_CHILDREN}>
          {(t(`project.channels.${channelKey}.services.list_items`) as unknown as CopiesProps['services']['list_items'])?.map((item, index) => (
            <motion.div key={index} variants={VARIANTS_FADE_FROM_BOTTOM_TO_TOP()}>
              <dt>{item.title}</dt>
              <dd>{item.description}</dd>
            </motion.div>
          ))}
        </motion.dl>
      </SectionServices>
    </Main>
  )
}

const Main = styled.main`
  background-color: var(--color-background--dark);
  color: var(--color-text--dark);
  grid-column: 1 / span 6;

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 12;
  }
`

const SectionMain = styled.section`
  width: var(--viewport-width);
  width: var(--viewport-width--dynamic);

  h2 {
    ${presetsFluid.font25_40()}
    ${designGrid({})}
    padding: ${vw(60, 'mobile')} 0 ${vw(100, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(60, 'tablet')} 0 ${vw(120, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(60, 'desktop')} 0 ${vw(120, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
			${presetsFluid.font40_rel()}
      padding: 60px 0 120px;
    }

    span {
      grid-column: 1 / span 6;

      ${mq.greaterThan('tablet')} {
        grid-column: 1 / span 10;
      }
    }
  }

  dt {
    border-top: 1px solid ${rgbAlpha('--color-white', .2)};
    ${presetsFluid.font35_50()}
    ${designGrid({})}
    padding: ${vw(100, 'mobile')} 0 ${vw(60, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(120, 'tablet')} 0 ${vw(60, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(120, 'desktop')} 0 ${vw(60, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      ${presetsFluid.font50_rel()}
      font-weight: 400;
      padding: 120px 0 60px;
    }

    span {
      grid-column: 1 / span 6;

      ${mq.greaterThan('tablet')} {
        grid-column: 1 / span 10;
      }
    }
  }

  dd {
    align-items: flex-start;
    border-top: 1px solid ${rgbAlpha('--color-white', .2)};
    display: flex;
    flex-direction: column;
    gap: ${vw(10, 'mobile')};
    justify-content: flex-start;
    padding: ${vw(20, 'mobile')} ${vw(10, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      gap: ${vw(10, 'tablet')};
      padding: ${vw(20, 'tablet')} ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      ${designGrid({})}
      padding: ${vw(30, 'desktop')} 0;
    }

    ${mq.greaterThan('desktop')} {
      padding: 30px 0;
    }

    &:first-of-type {
      border-top: none;
    }
  }

  h3,
  p,
  .medium-wrapper {
    ${mq.greaterThan('tablet')} {
      grid-row: 1 / span 1;
    }
  }

  h3 {
    text-align: left;
    ${presetsFluid.font25_30()}

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 5;
    }

    ${mq.greaterThan('desktop-md')} {
      grid-column: 1 / span 6;
    }

		${mq.greaterThan('desktop')} {
			${presetsFluid.font30_rel()}
		}
  }

  p {
    ${presetsFluid.font15()}
    opacity: .7;

    ${mq.greaterThan('tablet')} {
      grid-column: 6 / span 6;
      width: calc(100% - 160px);
    }

    ${mq.greaterThan('desktop-md')} {
      grid-column: 7 / span 6;
      max-width: 330px;
      width: calc(100% - 200px);
    }

		${mq.greaterThan('desktop')} {
			${presetsFluid.font15_rel()}
		}
  }

  .medium-wrapper {
    height: 150px;
    margin-right: auto;
    position: relative;
    width: 150px;

    ${mq.greaterThan('tablet')} {
      grid-column: 10 / span 3;
      margin-left: auto;
      margin-right: 0;
    }

    ${mq.greaterThan('desktop')} {
      height: 160px;
      width: 160px;
    }

    img {
      height: 100%;
      object-fit: cover;
      width: 100%;
      z-index: 2;
    }
  }
`

const SectionServices = styled.section`
  align-items: flex-start;
  border-top: 1px solid ${rgbAlpha('--color-white', .2)};
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    flex-direction: row;
  }

  ${mq.greaterThan('desktop-xs')} {
    width: var(--viewport-width);
    width: var(--viewport-width--dynamic);
  }

  hgroup {
    padding: ${vw(60, 'mobile')} ${vw(10, 'mobile')} ${vw(20, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(60, 'tablet')} ${vw(10, 'tablet')} ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      ${innerGrid(defaultGrid.tablet!)};
      padding: ${vw(30, 'desktop')} ${vw(30, 'desktop')} ${vw(100, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      ${innerGrid(defaultGrid.desktop!)};
      padding: 30px 30px 100px;
    }
  }

  hgroup,
  dl {
    width: 100vw;
    width: 100dvw;

    ${mq.greaterThan('tablet')} {
      flex-shrink: 0;
      width: 50vw;
      width: 50dvw;
    }

    ${mq.greaterThan('desktop-xs')} {
      width: var(--viewport-width-half);
      width: var(--viewport-width-half--dynamic);
    }
  }

  h2 {
    ${presetsFluid.font18()}
    margin-bottom: ${vw(10, 'mobile')};
    opacity: .4;
    text-align: left;

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 5;
      margin-bottom: ${vw(10, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
			${presetsFluid.font18_rel()}
      margin-bottom: 10px;
    }
  }

  p {
    ${presetsFluid.font35_50()}

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 10;
    }

    ${mq.greaterThan('desktop')} {
      ${presetsFluid.font50_rel()}
      font-weight: 400;
    }
  }

  dl {
    ${mq.greaterThan('tablet')} {
      border-left: 1px solid ${rgbAlpha('--color-white', .2)};
      height: -webkit-fill-available;
			height: min-content;
    }

    > div {
      align-items: flex-start;
      border-top: 1px solid ${rgbAlpha('--color-white', .2)};
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      gap: ${vw(20, 'mobile')};
      justify-content: flex-start;
      padding: ${vw(20, 'mobile')} ${vw(10, 'mobile')};

      &:first-child {
        ${mq.greaterThan('tablet')} {
          border-top: none;
        }
      }

      ${mq.greaterThan('nexus7')} {
        gap: ${vw(20, 'tablet')};
        padding: ${vw(20, 'tablet')} ${vw(10, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        flex-direction: row;
        gap: ${vw(20, 'desktop')};
        padding: ${vw(30, 'desktop')};
        ${innerGrid(defaultGrid.tablet!)};
      }

      ${mq.greaterThan('desktop')} {
        gap: 20px;
        ${innerGrid(defaultGrid.desktop!)};
        padding: 30px;
      }
    }
  }

  dt {
    ${presetsFluid.font25()}

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 7;
    }

		${mq.greaterThan('desktop')} {
			${presetsFluid.font25_rel()}
		}
  }

  dd {
    ${presetsFluid.font15()}
    opacity: .7;

    ${mq.greaterThan('tablet')} {
      grid-column: 9 / span 4;
    }

		${mq.greaterThan('desktop')} {
			${presetsFluid.font15_rel()}
		}
  }
`
